import { DatabaseService, TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { AWSRDSDatabase, Database, EC2Server, InterpretedDatabase, OutputEc2, PREINSTALLED_SOFTWARE } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Alert, Container, Grid, Header, Link, NonCancelableCustomEvent, SpaceBetween, Spinner, Toggle, ToggleProps } from '@amzn/awsui-components-react';
import React, { useCallback, useEffect, useState } from 'react';

import { constants } from '../../common/Localization';
import { format } from '../../common/util';
import { AwsEC2RecommendationDetail } from './ApplicationResourceDetailsAwsEC2';
import { formatPaymentOption, ValueWithLabel } from './utils';

type Props = {
    database: InterpretedDatabase<Database>;
    tcoAssessmentId?: string;
    portfolioId: string;
    onInfoLinkClick: (id: string) => void;
} & FlashContextInterface;

const tcoService = new TcoService();
const databaseService = new DatabaseService();
function ApplicationResourceDatabaseDetails({ database, tcoAssessmentId, portfolioId, onInfoLinkClick, flash }: Props) {
    const { alert } = flash;
    const [loading, setLoading] = useState(false);
    const [enableRDSRightSize, setEnableRDSRightSize] = useState(false);
    const [enableEC2Recommendations, setEnableEC2Recommendations] = useState(false); // if EC2 recommendations toggle in assumptions is enabled
    const [hasEC2Recommendation, setHasEC2Recommendation] = useState(false); // if database evaluates to EC2 with SQL server
    const [serverResult, setServerResult] = useState<EC2Server | undefined>(); // for EC2 with SQL server, EC2 output from tco assessment
    const [awsEC2result, setAwsEC2result] = useState<OutputEc2 | undefined>(); // for EC2 with SQL server, corresponding server from tco assessment
    const [isValidServer, setIsValidServer] = useState(false);
    const [isServerInScope, setIsServerInScope] = useState(false);
    const [hasRDSRecommendation, setHasRDSRecommendation] = useState(false);
    const [awsRDSDatabase, setAwdRDSDatabase] = useState<AWSRDSDatabase | null>(null);
    const [instanceType, setInstanceType] = useState<string | null>(null);
    const [instanceTypeVCpu, setInstanceTypeVCpu] = useState<number | null>(null);
    const [instanceTypeMemory, setInstanceTypeMemory] = useState<number | null>(null);
    const [volumeType, setVolumeType] = useState<string | null>(null);
    const [volumeSize, setVolumeSize] = useState(0);

    const [inScope, setInScope] = useState(database.inScope);
    const inScopeChange = async (event: NonCancelableCustomEvent<ToggleProps.ChangeDetail>) => {
        const { checked } = event.detail;
        setInScope(checked);
        try {
            await databaseService.updateInScope(database.databaseId, portfolioId, checked);
        } catch (err) {
            alert(FlashType.error, (err as Error).message, constants.ENCOUNTERED_ERROR_UPDATING_DB_INSCOPE);
            setInScope(!checked);
        }
    };

    /**
     * Retrieve the EC2 TCO output to check if the database evaluates as EC2 with SQL server, based on the corresponding server
     */
    const getEC2Recommendation = useCallback( async (): Promise<{awsEC2result: OutputEc2, serverResult: EC2Server} | undefined> => {
        try {
            if (tcoAssessmentId && tcoAssessmentId !== 'default') {
                const awsEC2result: OutputEc2 = (await tcoService.getAwsEC2(tcoAssessmentId)).output?.aws?.ec2;
                if (!awsEC2result) {
                    return;
                }
                const serverResult = awsEC2result.servers.find(ec2 => ec2.serverId === database.serverId);
                if (!serverResult) {
                    return;
                }
            
                return {
                    awsEC2result,
                    serverResult
                };
            }
            return;
        } catch (err) {
            alert(FlashType.error, (err as Error).message, constants.ENCOUNTERED_ERROR_LOADING_APP_DB_DETAILS);
            return;
        }
    }, [tcoAssessmentId, database.serverId, alert]);

    const loadData = useCallback( async () => {
        if (!tcoAssessmentId) {
            return;
        }

        setLoading(true);
        try {
            const tcoResult = await tcoService.get(tcoAssessmentId);
            const rdsRecommendation = tcoResult?.configuration?.awsRDSRightSize?.enableRDSRightSize;
            const ec2Recommendation = tcoResult?.configuration?.awsRDSRightSize?.enableEC2Recommendations;

            setEnableRDSRightSize(rdsRecommendation);
            setEnableEC2Recommendations(ec2Recommendation);

            if (!rdsRecommendation && !ec2Recommendation) {
                return;
            }

            // EC2 with SQL server takes precedence over RDS recommendation
            if (ec2Recommendation) {
                const ec2result = await getEC2Recommendation();
                // check to tell if database evaluates to EC2 with SQL server
                if (ec2result?.serverResult?.preInstalledSw && ec2result.serverResult.preInstalledSw !== PREINSTALLED_SOFTWARE.na) {
                    setHasEC2Recommendation(true);
                    setAwsEC2result(ec2result.awsEC2result);
                    setServerResult(ec2result.serverResult);
                    return;
                }
            }

            if (database.interpretedFields) {
                setIsValidServer(true);
            } else {
                setIsValidServer(false);
                return;
            }

            if (database.interpretedFields.isServerInScope.value) {
                setIsServerInScope(true);
            } else {
                setIsServerInScope(false);
                return;
            }

            // Check for RDS recommendation for this database
            const awsRDSResult = (await tcoService.getAwsRDS(tcoAssessmentId)).output?.aws?.rds;
            const awsRDSForDatabase = awsRDSResult.databases.find(db => db.databaseId === database.databaseId);
            if (awsRDSForDatabase) {
                setHasRDSRecommendation(true);
                setAwdRDSDatabase(awsRDSForDatabase);
                setInstanceType(awsRDSForDatabase.instanceType);
                const instanceTypeObject = awsRDSResult.instanceTypes.find(type => type.instanceType === awsRDSForDatabase.instanceType);

                if (instanceTypeObject) {
                    setInstanceTypeVCpu(instanceTypeObject.cpu);
                    setInstanceTypeMemory(instanceTypeObject.memory);
                }

                const awsStorageRDS = (await tcoService.getAwsStorage(tcoAssessmentId)).output?.aws?.storage?.rds;
                const awsStorageRDSDatabase = awsStorageRDS && awsStorageRDS.databases
                    && awsStorageRDS.databases.find(db => db.databaseId === database.databaseId);
                if (awsStorageRDSDatabase) {
                    setVolumeSize(awsStorageRDSDatabase.storageSize);
                    const dbVolumeType = awsStorageRDSDatabase.volumeType;
                    if (dbVolumeType?.length > 0) {
                        setVolumeType(dbVolumeType);
                    } else {
                        setVolumeType(null);
                    }
                }
            } else {
                setHasRDSRecommendation(false);
            }
        } catch (err) {
            alert(FlashType.error, (err as Error).message, constants.ENCOUNTERED_ERROR_LOADING_APP_DB_DETAILS);
        } finally {
            setLoading(false);
        }
    }, [database.databaseId, database.interpretedFields, alert, tcoAssessmentId, getEC2Recommendation]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const infoLinkClick = () => onInfoLinkClick('applicationResourcesDatabaseDetails');

    const utilizationMethodText = database.interpretedFields && (
        `${constants.UTILIZATION_METHOD} ${database.interpretedFields.utilizationMethod.label}`
    );
    const databaseDetailHeader = (
        <Grid gridDefinition={[{colspan: 12}, {colspan: 12}]} disableGutters>
            <SpaceBetween size='xs' data-testid='databaseDetailHeader'>
                <SpaceBetween direction='horizontal' size='xs'>
                    <strong>{database.databaseId}</strong>
                    <Link variant='info' onFollow={infoLinkClick}>Info</Link>
                </SpaceBetween>
                <Toggle
                    checked={inScope || false}
                    onChange={inScopeChange}
                >
                    {constants.IN_SCOPE_OF_PORTFOLIO}
                </Toggle>
            </SpaceBetween>
            {utilizationMethodText}
        </Grid>
    );

    const rdSNotEnabledContent = (
        <Grid gridDefinition={[{colspan: 12}]} data-testid='rdsNotEnabled'>
            {constants.MPA_ONLY_HANDLES_DB_FOR_RDS_OR_EC2_RECOMMENDATION}
        </Grid>
    );

    const serverNotValid = (
        <Grid gridDefinition={[{colspan: 12}]} data-testid='serverNotValid'>
            <div>
                {format(constants.SERVER_WITH_ID_DOES_NOT_EXIST, [database.serverId])}
                {constants.YOU_CAN_ADD_SERVER_OR_UPDATE_DB_SERVER_ID}
            </div>
        </Grid>
    );

    const serverNotInScope = (
        <Grid gridDefinition={[{colspan: 12}]} data-testid='serverNotInScope'>
            <div>
                {format(constants.SERVER_WITH_ID_IS_OUT_OF_SCOPE, [database.serverId])}
                {constants.YOU_CAN_SET_SERVER_IN_SCOPE}
            </div>
        </Grid>
    );

    const onPremVsAWSHeader = (
        <Header variant='h2'>
            {constants.ON_PREM_VS_AWS}
        </Header>
    );

    const onPremHeader = (
        <Header variant='h2'>
            {constants.ON_PREMISES}
        </Header>
    );

    const onPremContainerGridDefinition = [{colspan: 6}, {colspan: 6}, {colspan: 12}, {colspan: 12}, {colspan: 12}, {colspan: 12}];
    const onPremContainer = (
        <Container
            data-testid='onPremContainer'
            header={onPremHeader}
        >
            <Grid
                gridDefinition={onPremContainerGridDefinition}
            >
                <ValueWithLabel label={constants.VIRTUALIZATION}>{database.interpretedFields?.isPhysical.label}</ValueWithLabel>
                <ValueWithLabel label={constants.OS}>{database.interpretedFields?.os.label}</ValueWithLabel>
                <ValueWithLabel label={constants.DB_ENGINE}>{database.dbInstanceName} {database.dbInstanceEngine} {database.edition}</ValueWithLabel>
                <ValueWithLabel label={constants.LICENSE_MODEL}>{constants.BRING_YOUR_OWN_LICENSE}</ValueWithLabel>
                <ValueWithLabel label={constants.SPECS}>
                    {database?.interpretedFields?.numCores.label} / {database?.interpretedFields?.ramSize.label}
                </ValueWithLabel>
                <ValueWithLabel label={constants.STORAGE}>
                    {database.interpretedFields?.storageSizeGB.label} / {database?.interpretedFields?.storageMaxIops.label}
                </ValueWithLabel>
            </Grid>
        </Container>
    );

    const awsHeader = (
        <Header variant='h2'>
            {constants.IN_AWS}
        </Header>
    );

    let volumeDisplayText = database.interpretedFields?.storageMaxIops.label;
    volumeDisplayText = volumeSize ? `${volumeSize} GB / ${volumeDisplayText}` : volumeDisplayText;
    volumeDisplayText = volumeType ? `${volumeType} / ${volumeDisplayText}` : volumeDisplayText;

    const getRecalculateTcoMessage = () => (
        <Alert header={constants.RECALCULATE_TCO_MESSAGE_HEADER}>
            {constants.RECALCULATE_TCO_MESSAGE}
        </Alert>
    );

    const awsContainerGridDefinition = [{colspan: 12}, {colspan: 6}, {colspan: 6}, {colspan: 6}, {colspan: 6}, {colspan: 12}, {colspan: 12}];
    const getAWSContent = (): JSX.Element | string => {
        if (!tcoAssessmentId) {
            return getRecalculateTcoMessage();
        }
        if (hasEC2Recommendation) {
            return (
                <AwsEC2RecommendationDetail
                    tcoAssessmentId={tcoAssessmentId}
                    awsEC2result={awsEC2result}
                    server={serverResult}
                />
            );
        }
        if (hasRDSRecommendation && awsRDSDatabase) {
            return (
                <Grid gridDefinition={awsContainerGridDefinition}>
                    <ValueWithLabel label={constants.AWS_RECOMMENDATION}>{constants.RDS}</ValueWithLabel>
                    <ValueWithLabel label={constants.RECOMMENDED_PAYMENT_OPTION}>
                        {formatPaymentOption(awsRDSDatabase.recommendedPaymentOption)}
                    </ValueWithLabel>
                    <ValueWithLabel label={constants.DB_ENGINE}>
                        {awsRDSDatabase.instanceAttributes.databaseEngine} {awsRDSDatabase.instanceAttributes.databaseEdition}
                    </ValueWithLabel>
                    <ValueWithLabel label={constants.LICENSE_MODEL}>{awsRDSDatabase.instanceAttributes.licenseModel}</ValueWithLabel>
                    <ValueWithLabel label={constants.DEPLOYMENT_OPTION}>{awsRDSDatabase.instanceAttributes.deploymentOption}</ValueWithLabel>
                    <ValueWithLabel label={constants.INSTANCE}>
                        {instanceType} / {instanceTypeVCpu} {constants.V_CPUS} / {instanceTypeMemory} {constants.GIB}
                    </ValueWithLabel>
                    <ValueWithLabel label={constants.VOLUME}>{volumeDisplayText}</ValueWithLabel>
                </Grid>
            );
        }
        return constants.RECOMMENDATION_NOT_MADE_FOR_DB;
    };

    const allContent = (
        <Grid
            gridDefinition={[{colspan: 12}, {colspan: 12}]}
            data-testid='allContent'
        >
            {onPremVsAWSHeader}
            <Grid gridDefinition={[{colspan: 6}, {colspan: 6}]}>
                {onPremContainer}
                <Container
                    header={awsHeader}
                >
                    {getAWSContent()}
                </Container>
            </Grid>
        </Grid>
    );

    function getContent(): JSX.Element {
        if (!tcoAssessmentId) {
            return allContent;
        }
        if (!enableRDSRightSize && !enableEC2Recommendations) {
            return rdSNotEnabledContent;
        }
        if (hasEC2Recommendation) {
            return allContent;
        }
        if (!isValidServer) {
            return serverNotValid;
        }
        if (!isServerInScope) {
            return serverNotInScope;
        }
        return allContent;
    }

    const databaseDetailsOrLoading = loading ? (
        <Spinner size='large' data-testid='loading'/>
    ) : (
        getContent()
    );

    const databaseDetailsContent = (
        <Grid data-testid='content'
            id='databaseDetails'
            gridDefinition={[{colspan: 12 }, {colspan: 12 }]}
        >
            {databaseDetailHeader}
            {databaseDetailsOrLoading}
        </Grid>
    );

    return (
        <Container>
            {databaseDetailsContent}
        </Container>
    );
}

export default withFlashContext(ApplicationResourceDatabaseDetails);
